/** Universal report builder types (mirrors backend ReportDefinition). */

export type SourceKind = 'entity' | 'virtual' | 'external';
export type JoinKind = 'many_to_one' | 'one_to_many' | 'inner' | 'left';
export type FieldRole = 'detail' | 'group' | 'aggregate' | 'calc';
export type FieldAxis = 'row' | 'col';
export type HeaderDisplay = 'column' | 'indent';
export type AggregateFn = 'SUM' | 'COUNT' | 'AVG' | 'MIN' | 'MAX';
export type FilterLogic = 'and' | 'or';
export type FilterOp =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'filled'
  | 'empty'
  | 'contains'
  | 'in'
  | 'not_in';
export type PresentationType = 'table' | 'tree' | 'crosstab' | 'chart';

export interface ReportSource {
  id: string;
  kind: SourceKind;
  alias: string;
  object_name?: string | null;
  provider?: string | null;
  display_name?: string | null;
  external_node_id?: string | null;
  entity_set?: string | null;
}

/** Canonical metadata source_ref for any source kind (shared by palette/data/ask). */
export function sourceRef(source: ReportSource): string {
  if (source.kind === 'external' && source.external_node_id && source.entity_set) {
    return `ext/${source.external_node_id}/${source.entity_set}`;
  }
  if (source.kind === 'virtual' && source.object_name && source.provider) {
    return `${source.object_name}/${source.provider}`;
  }
  return source.object_name || '';
}

export interface ReportJoin {
  id: string;
  left: string;
  right: string;
  kind: JoinKind;
}

export interface FilterCondition {
  name: string;
  field: string;
  op: string;
  required?: boolean;
  value: { kind: 'ask' } | { kind: 'fixed'; value?: unknown };
  synonyms?: Record<string, string>;
}

export interface FilterGroup {
  logic: FilterLogic;
  children: Array<FilterCondition | FilterGroup>;
}

export interface CompositionField {
  id: string;
  name: string;
  expression: string;
  role: FieldRole;
  axis?: FieldAxis | null;
  header_display?: HeaderDisplay;
  visible?: boolean;
  aggregate?: AggregateFn | null;
  format?: string | null;
  synonyms?: Record<string, string>;
}

export interface FormulaToken {
  ref?: string | null;
  op?: string | null;
  lit?: number | null;
}

export type StructureRowKind = 'filter' | 'formula' | 'group' | 'header' | 'expand';
export type ExpandOrder = 'label' | 'value_desc' | 'value_asc';

export interface StructureRowStyle {
  bold?: boolean;
  indent?: number;
}

export interface StructureExpand {
  by_field_id: string;
  filter?: FilterGroup | null;
  order_by?: ExpandOrder;
  limit?: number | null;
}

export interface StructureRow {
  id: string;
  name: string;
  kind: StructureRowKind;
  parent?: string | null;
  order?: number;
  visible?: boolean;
  format?: string | null;
  style?: StructureRowStyle;
  filter?: FilterGroup | null;
  measure_field_id?: string | null;
  formula_tokens?: FormulaToken[];
  expand?: StructureExpand | null;
}

export interface StructurePeriod {
  years?: number;
  months?: number;
  days?: number;
  anchor?: 'start' | 'end' | null;
}

export interface StructureColumn {
  id: string;
  name: string;
  measure_field_id: string;
  filter?: FilterGroup | null;
  period?: StructurePeriod | null;
  format?: string | null;
  visible?: boolean;
}

export interface StructureBlank {
  rows: StructureRow[];
  columns: StructureColumn[];
  hide_empty_rows?: boolean;
}

/** Must match backend `CELL_SEP` in core/reports/structure.py. */
export const STRUCTURE_CELL_SEP = '\x1e';

export function structureCellKey(rowId: string, colId: string): string {
  return `${rowId}${STRUCTURE_CELL_SEP}${colId}`;
}

export const emptyStructureBlank = (): StructureBlank => ({
  rows: [], columns: [], hide_empty_rows: false,
});

export interface PresentationWidget {
  id: string;
  type: PresentationType;
  title: string;
  slice?: {
    row_field_ids?: string[];
    col_field_ids?: string[];
    measure_ids?: string[];
    indicator_ids?: string[];
  };
  chart?: {
    type: 'bar' | 'line' | 'area' | 'pie';
    category_field_id?: string | null;
    series_field_ids?: string[];
  };
  layout?: Record<string, unknown>;
}

export interface ReportOptions {
  limit?: number | null;
  preview_limit?: number | null;
  timeout_sec?: number | null;
  cache_ttl_sec?: number | null;
  truncate_on_limit?: boolean;
  debug?: boolean;
}

/** LavkaLang source defining on_prepare / on_render. */
export interface ReportHooks {
  source?: string;
}

export interface ReportDefinition {
  name: string;
  dataset: {
    sources: ReportSource[];
    joins: ReportJoin[];
    ask_filters: FilterGroup;
    fixed_filters: FilterGroup;
  };
  composition: { fields: CompositionField[] };
  structure?: StructureBlank | null;
  presentations: PresentationWidget[];
  options: ReportOptions;
  hooks?: ReportHooks;
  meta?: Record<string, unknown>;
}

export function emptyReportDefinition(name = 'New report'): ReportDefinition {
  return {
    name,
    dataset: {
      sources: [],
      joins: [],
      ask_filters: { logic: 'and', children: [] },
      fixed_filters: { logic: 'and', children: [] },
    },
    composition: { fields: [] },
    structure: emptyStructureBlank(),
    presentations: [],
    options: { truncate_on_limit: true },
    hooks: { source: '' },
    meta: {},
  };
}

export function normalizeReportDefinition(
  raw: Partial<ReportDefinition> | null | undefined,
  fallbackName?: string,
): ReportDefinition {
  const base = emptyReportDefinition(fallbackName ?? 'New report');
  if (!raw || typeof raw !== 'object') return base;
  return {
    ...base,
    ...raw,
    name: raw.name || base.name,
    dataset: {
      ...base.dataset,
      ...(raw.dataset || {}),
      sources: raw.dataset?.sources ?? base.dataset.sources,
      joins: raw.dataset?.joins ?? base.dataset.joins,
      ask_filters: raw.dataset?.ask_filters ?? base.dataset.ask_filters,
      fixed_filters: raw.dataset?.fixed_filters ?? base.dataset.fixed_filters,
    },
    composition: {
      fields: raw.composition?.fields ?? base.composition.fields,
    },
    structure: {
      rows: raw.structure?.rows ?? [],
      columns: raw.structure?.columns ?? [],
      hide_empty_rows: raw.structure?.hide_empty_rows ?? false,
    },
    presentations: raw.presentations ?? base.presentations,
    options: { ...base.options, ...(raw.options || {}) },
    hooks: { source: '', ...(raw.hooks || {}) },
  };
}
